import { Injectable } from '@angular/core';
import { EditorSnapshotV2 } from '../../../../models/other/editor-snapshot-v2.interface';

/**
 * Undo/redo history for the v2 template editor. Provided on
 * TemplateEditorV2Component (like ReviewStoreV2), so every editor instance
 * gets its own history and drops it on destroy.
 */
@Injectable()
export class EditorHistoryV2 {
  /** Oldest entries are dropped once the undo stack grows past this. */
  private static readonly LIMIT = 100;
  /** Changes with the same group key closer together than this merge into one step. */
  private static readonly GROUP_WINDOW_MS = 1000;

  /** Past states, newest last. */
  private undoStack: EditorSnapshotV2[] = [];
  /** States that were undone, newest last. */
  private redoStack: EditorSnapshotV2[] = [];
  /** The state currently on screen. */
  private current: EditorSnapshotV2 | null = null;

  /** Group key and time of the last record(), used to merge typing bursts. */
  private lastGroupKey?: string;
  private lastRecordAt = 0;

  /** Clears both stacks and makes `snapshot` the new baseline (load, save). */
  reset(snapshot: EditorSnapshotV2): void {
    this.undoStack = [];
    this.redoStack = [];
    this.current = snapshot;
    this.lastGroupKey = undefined;
    this.lastRecordAt = 0;
  }

  /**
   * Records the state after a change.
   *
   * Field edits pass a `groupKey` (e.g. `edit:<blockId>:<field>`): while the
   * same key keeps arriving within GROUP_WINDOW_MS, the open step is updated
   * instead of a new one being pushed, so a typing burst is one undo step.
   * Structural changes (add, remove, reorder...) pass no key and always push.
   */
  record(snapshot: EditorSnapshotV2, groupKey?: string): void {
    // Nothing to compare against yet (reset() wasn't called): just start here.
    if (!this.current) {
      this.current = snapshot;
      return;
    }

    // Some emits (e.g. Quill normalising its HTML) don't change anything.
    if (this.isSame(this.current, snapshot)) {
      return;
    }

    const now = Date.now();
    const continuesGroup =
      groupKey !== undefined &&
      groupKey === this.lastGroupKey &&
      now - this.lastRecordAt < EditorHistoryV2.GROUP_WINDOW_MS;

    if (!continuesGroup) {
      // New step: the state before this change becomes undoable.
      this.undoStack.push(this.current);
      if (this.undoStack.length > EditorHistoryV2.LIMIT) {
        this.undoStack.shift();
      }
    }
    // When it continues the group, the state before the burst is already on
    // the undo stack, so only `current` moves forward.

    this.current = snapshot;
    this.redoStack = [];
    this.lastGroupKey = groupKey;
    this.lastRecordAt = now;
  }

  /** Returns the snapshot to restore, or null when there is nothing to undo. */
  undo(): EditorSnapshotV2 | null {
    // Bail out before touching anything, so an empty stack can't lose `current`.
    if (!this.current || !this.undoStack.length) {
      return null;
    }
    this.redoStack.push(this.current);
    this.current = this.undoStack.pop()!;
    // The next edit starts a new step instead of merging into the restored one.
    this.lastGroupKey = undefined;
    return this.current;
  }

  /** Returns the snapshot to restore, or null when there is nothing to redo. */
  redo(): EditorSnapshotV2 | null {
    if (!this.current || !this.redoStack.length) {
      return null;
    }
    this.undoStack.push(this.current);
    this.current = this.redoStack.pop()!;
    this.lastGroupKey = undefined;
    return this.current;
  }

  /** True when undo() would restore something. */
  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /** True when redo() would restore something. */
  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Blocks are JSON strings, so comparing them is a plain `===` per block. */
  private isSame(a: EditorSnapshotV2, b: EditorSnapshotV2): boolean {
    return (
      this.sameStrings(a.blocks, b.blocks) &&
      this.sameStrings(a.mediaForDeletion, b.mediaForDeletion) &&
      this.sameStrings(a.mediaForDeletionWithinABlock, b.mediaForDeletionWithinABlock)
    );
  }

  private sameStrings(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((value, i) => value === b[i]);
  }
}
